import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Edit3,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X
} from "lucide-react";
import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  createWorkspaceMVPCompany,
  createWorkspaceMVPFlow,
  createWorkspaceMVPLead,
  createWorkspaceMVPOffer,
  createWorkspaceMVPOrder,
  createWorkspaceMVPPayout,
  createWorkspaceMVPPostback,
  createWorkspaceMVPUser,
  deleteWorkspaceMVPCompany,
  deleteWorkspaceMVPFlow,
  deleteWorkspaceMVPLead,
  deleteWorkspaceMVPOffer,
  deleteWorkspaceMVPOrder,
  deleteWorkspaceMVPPayout,
  deleteWorkspaceMVPPostback,
  deleteWorkspaceMVPUser,
  fetchWorkspaceMVPStats,
  listWorkspaceMVPCompanies,
  listWorkspaceMVPFlows,
  listWorkspaceMVPLeads,
  listWorkspaceMVPOffers,
  listWorkspaceMVPOrders,
  listWorkspaceMVPPayouts,
  listWorkspaceMVPPostbacks,
  listWorkspaceMVPUsers,
  updateWorkspaceMVPCompany,
  updateWorkspaceMVPFlow,
  updateWorkspaceMVPLead,
  updateWorkspaceMVPOffer,
  updateWorkspaceMVPOrder,
  updateWorkspaceMVPPayout,
  updateWorkspaceMVPPostback,
  updateWorkspaceMVPUser,
  type ActorRole,
  type Workspace,
  type WorkspaceMVPCompany,
  type WorkspaceMVPCompanyPayload,
  type WorkspaceMVPCompanyStatus,
  type WorkspaceMVPFlow,
  type WorkspaceMVPFlowPayload,
  type WorkspaceMVPLead,
  type WorkspaceMVPLeadPayload,
  type WorkspaceMVPLeadStatus,
  type WorkspaceMVPOffer,
  type WorkspaceMVPOfferPayload,
  type WorkspaceMVPOfferStatus,
  type WorkspaceMVPOrder,
  type WorkspaceMVPOrderPayload,
  type WorkspaceMVPOrderStatus,
  type WorkspaceMVPPayout,
  type WorkspaceMVPPayoutMethod,
  type WorkspaceMVPPayoutPayload,
  type WorkspaceMVPPayoutStatus,
  type WorkspaceMVPPostback,
  type WorkspaceMVPPostbackPayload,
  type WorkspaceMVPPostbackStatus,
  type WorkspaceMVPStats,
  type WorkspaceMVPUser,
  type WorkspaceMVPUserPayload,
  type WorkspaceMVPUserRole,
  type WorkspaceMVPUserStatus,
  type WorkspaceMVPWorkspace,
  type WorkspaceSummary
} from "../lib/api";
import { AdminUsersPanel } from "./AdminUsersPanel";
import { BulkLeadJobsPanel } from "./BulkLeadJobsPanel";
import { Button } from "./ui/button";
import "./mvpWorkspacePages.css";

type WorkspaceSection = "overview" | "work" | "entities" | "reports" | "settings" | "users";

type MvpWorkspacePagesProps = {
  actorRole: ActorRole;
  activeSection: WorkspaceSection;
  onSectionNavigate: (path: string) => void;
  summary: WorkspaceSummary;
  workspace: Workspace;
};

type CopySet = typeof ruCopy;

type FlowDraft = {
  id: string;
  offer_id: string;
  name: string;
  status: WorkspaceMVPOfferStatus;
  traffic_source: string;
  landing_url: string;
  daily_cap: string;
};

type OfferDraft = {
  id: string;
  company_id: string;
  name: string;
  status: WorkspaceMVPOfferStatus;
  geo: string;
  payout: string;
  currency: string;
};

type LeadDraft = {
  id: string;
  offer_id: string;
  flow_id: string;
  customer_ref: string;
  country: string;
  source: string;
  status: WorkspaceMVPLeadStatus;
};

type PostbackDraft = {
  id: string;
  flow_id: string;
  event: string;
  target_url: string;
  status: WorkspaceMVPPostbackStatus;
};

type PayoutDraft = {
  id: string;
  user_id: string;
  amount: string;
  currency: string;
  method: WorkspaceMVPPayoutMethod;
  status: WorkspaceMVPPayoutStatus;
};

type CompanyDraft = {
  id: string;
  name: string;
  status: WorkspaceMVPCompanyStatus;
  plan: string;
};

type UserDraft = {
  id: string;
  email: string;
  display_name: string;
  role: WorkspaceMVPUserRole;
  company_id: string;
  status: WorkspaceMVPUserStatus;
};

const offerStatuses: WorkspaceMVPOfferStatus[] = ["active", "review", "paused", "archived"];
const leadStatuses: WorkspaceMVPLeadStatus[] = ["new", "validated", "accepted", "rejected", "hold"];
const orderStatuses: WorkspaceMVPOrderStatus[] = ["pending", "approved", "cancelled", "refunded"];
const postbackStatuses: WorkspaceMVPPostbackStatus[] = ["active", "paused", "failed"];
const payoutStatuses: WorkspaceMVPPayoutStatus[] = ["pending", "approved", "paid", "rejected"];
const companyStatuses: WorkspaceMVPCompanyStatus[] = ["active", "review", "suspended"];
const userStatuses: WorkspaceMVPUserStatus[] = ["active", "invited", "suspended"];
const payoutMethods: WorkspaceMVPPayoutMethod[] = ["manual", "bank_transfer", "crypto"];
const userRoles: WorkspaceMVPUserRole[] = [
  "webmaster",
  "advertiser_owner",
  "advertiser_manager",
  "platform_support",
  "platform_admin"
];

const ruCopy = {
  titles: {
    webmaster: "Кабинет веб-мастера",
    advertiser: "Кабинет рекламодателя",
    admin: "Админ-панель"
  },
  subtitles: {
    webmaster: "Офферы, потоки, постбэки, лиды и выплаты для ежедневной работы.",
    advertiser: "Офферы, лиды, заказы, сотрудники и постбэки компании.",
    admin: "Компании, пользователи, модерация офферов, капы и аудит."
  },
  actions: {
    add: "Добавить",
    approve: "Одобрить",
    archive: "Архив",
    cancel: "Отмена",
    copy: "Скопировать",
    create: "Создать",
    delete: "Удалить",
    edit: "Редактировать",
    refresh: "Обновить",
    save: "Сохранить",
    search: "Поиск",
    suspend: "Отключить"
  },
  fields: {
    amount: "Сумма",
    company: "Компания",
    country: "Страна",
    currency: "Валюта",
    dailyCap: "Дневной кап",
    email: "Email",
    event: "Событие",
    flow: "Поток",
    geo: "GEO",
    landing: "Лендинг",
    method: "Метод",
    name: "Название",
    offer: "Оффер",
    payout: "Выплата",
    plan: "План",
    role: "Роль",
    source: "Источник",
    status: "Статус",
    target: "URL постбэка",
    user: "Пользователь"
  },
  labels: {
    actions: "Действия",
    advertiserStaff: "Команда рекламодателя",
    audit: "Последние действия",
    availableOffers: "Доступные офферы",
    caps: "Капы и маршруты",
    companies: "Компании",
    companyForm: "Компания",
    empty: "Записей пока нет.",
    flows: "Потоки",
    integration: "Постбэки и интеграции",
    leads: "Лиды",
    moderation: "Модерация офферов",
    online: "Работает",
    orders: "Заказы",
    payouts: "Выплаты",
    postbacks: "Постбэки",
    reports: "Отчеты",
    requiresAttention: "Требует внимания",
    trackingLink: "Трекинговая ссылка",
    userForm: "Пользователь",
    users: "Пользователи"
  },
  metrics: {
    companies: "Компании",
    flows: "Потоки",
    leads: "Лиды",
    offers: "Офферы",
    orders: "Заказы",
    payouts: "Выплаты",
    postbacks: "Постбэки",
    users: "Пользователи"
  },
  messages: {
    copied: "Ссылка скопирована.",
    loadFailed: "Не удалось загрузить данные кабинета.",
    loading: "Загрузка данных...",
    needCompany: "Сначала добавьте компанию.",
    needFlow: "Сначала добавьте поток.",
    needLead: "Сначала добавьте лид.",
    needOffer: "Сначала добавьте оффер.",
    needUser: "Сначала добавьте пользователя.",
    saved: "Изменения сохранены."
  },
  roles: {
    advertiser_manager: "Менеджер рекламодателя",
    advertiser_owner: "Владелец рекламодателя",
    platform_admin: "Админ платформы",
    platform_support: "Поддержка платформы",
    webmaster: "Веб-мастер"
  },
  status: {
    accepted: "Принят",
    active: "Активно",
    approved: "Одобрен",
    archived: "Архив",
    cancelled: "Отменен",
    failed: "Ошибка",
    hold: "Холд",
    invited: "Приглашен",
    new: "Новый",
    paid: "Выплачен",
    paused: "Пауза",
    pending: "Ожидает",
    refunded: "Возврат",
    rejected: "Отклонен",
    review: "Проверка",
    suspended: "Отключен",
    validated: "Проверен"
  }
};

const enCopy = {
  titles: {
    webmaster: "Webmaster workspace",
    advertiser: "Advertiser workspace",
    admin: "Platform admin"
  },
  subtitles: {
    webmaster: "Offers, flows, postbacks, leads and payouts for daily work.",
    advertiser: "Company offers, leads, orders, staff and postbacks.",
    admin: "Companies, users, offer moderation, caps and audit."
  },
  actions: {
    add: "Add",
    approve: "Approve",
    archive: "Archive",
    cancel: "Cancel",
    copy: "Copy",
    create: "Create",
    delete: "Delete",
    edit: "Edit",
    refresh: "Refresh",
    save: "Save",
    search: "Search",
    suspend: "Suspend"
  },
  fields: {
    amount: "Amount",
    company: "Company",
    country: "Country",
    currency: "Currency",
    dailyCap: "Daily cap",
    email: "Email",
    event: "Event",
    flow: "Flow",
    geo: "GEO",
    landing: "Landing",
    method: "Method",
    name: "Name",
    offer: "Offer",
    payout: "Payout",
    plan: "Plan",
    role: "Role",
    source: "Source",
    status: "Status",
    target: "Postback URL",
    user: "User"
  },
  labels: {
    actions: "Actions",
    advertiserStaff: "Advertiser team",
    audit: "Recent actions",
    availableOffers: "Available offers",
    caps: "Caps and routing",
    companies: "Companies",
    companyForm: "Company",
    empty: "No records yet.",
    flows: "Flows",
    integration: "Postbacks and integrations",
    leads: "Leads",
    moderation: "Offer moderation",
    online: "Online",
    orders: "Orders",
    payouts: "Payouts",
    postbacks: "Postbacks",
    reports: "Reports",
    requiresAttention: "Needs attention",
    trackingLink: "Tracking link",
    userForm: "User",
    users: "Users"
  },
  metrics: {
    companies: "Companies",
    flows: "Flows",
    leads: "Leads",
    offers: "Offers",
    orders: "Orders",
    payouts: "Payouts",
    postbacks: "Postbacks",
    users: "Users"
  },
  messages: {
    copied: "Link copied.",
    loadFailed: "Could not load workspace data.",
    loading: "Loading data...",
    needCompany: "Create a company first.",
    needFlow: "Create a flow first.",
    needLead: "Create a lead first.",
    needOffer: "Create an offer first.",
    needUser: "Create a user first.",
    saved: "Saved."
  },
  roles: {
    advertiser_manager: "Advertiser manager",
    advertiser_owner: "Advertiser owner",
    platform_admin: "Platform admin",
    platform_support: "Platform support",
    webmaster: "Webmaster"
  },
  status: {
    accepted: "Accepted",
    active: "Active",
    approved: "Approved",
    archived: "Archived",
    cancelled: "Cancelled",
    failed: "Failed",
    hold: "Hold",
    invited: "Invited",
    new: "New",
    paid: "Paid",
    paused: "Paused",
    pending: "Pending",
    refunded: "Refunded",
    rejected: "Rejected",
    review: "Review",
    suspended: "Suspended",
    validated: "Validated"
  }
};

export function MvpWorkspacePages({
  actorRole,
  activeSection,
  onSectionNavigate,
  summary,
  workspace
}: MvpWorkspacePagesProps) {
  const { i18n } = useTranslation("common");
  const copy = i18n.language.startsWith("ru") ? ruCopy : enCopy;
  const queryClient = useQueryClient();
  const data = useWorkspaceMVPData();
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [flowDraft, setFlowDraft] = useState<FlowDraft>(emptyFlowDraft());
  const [offerDraft, setOfferDraft] = useState<OfferDraft>(emptyOfferDraft());
  const [leadDraft, setLeadDraft] = useState<LeadDraft>(emptyLeadDraft());
  const [postbackDraft, setPostbackDraft] = useState<PostbackDraft>(emptyPostbackDraft());
  const [payoutDraft, setPayoutDraft] = useState<PayoutDraft>(emptyPayoutDraft());
  const [companyDraft, setCompanyDraft] = useState<CompanyDraft>(emptyCompanyDraft());
  const [userDraft, setUserDraft] = useState<UserDraft>(emptyUserDraft());
  const statusText = summary.health === "ok" ? copy.labels.online : copy.labels.requiresAttention;
  const canManagePlatform = actorRole === "platform_admin";

  const filteredLeads = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) {
      return data.leads;
    }
    return data.leads.filter((lead) =>
      [lead.id, lead.customer_ref, lead.country, lead.source, getOfferName(data.offers, lead.offer_id), getFlowName(data.flows, lead.flow_id)]
        .join(" ")
        .toLowerCase()
        .includes(normalized)
    );
  }, [data.flows, data.leads, data.offers, search]);

  async function invalidateWorkspaceMVP() {
    await queryClient.invalidateQueries({ queryKey: ["workspace-mvp"] });
  }

  async function runAction(action: () => Promise<void>, successMessage = copy.messages.saved) {
    setError("");
    setNotice("");
    try {
      await action();
      await invalidateWorkspaceMVP();
      setNotice(successMessage);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed");
    }
  }

  async function handleFlowSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const payload = buildFlowPayload(flowDraft, data.offers, copy);
    await runAction(async () => {
      if (flowDraft.id) {
        await updateWorkspaceMVPFlow(flowDraft.id, payload);
      } else {
        await createWorkspaceMVPFlow(payload);
      }
      setFlowDraft(emptyFlowDraft());
    });
  }

  async function handleOfferSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const payload = buildOfferPayload(offerDraft, data.companies, copy);
    await runAction(async () => {
      if (offerDraft.id) {
        await updateWorkspaceMVPOffer(offerDraft.id, payload);
      } else {
        await createWorkspaceMVPOffer(payload);
      }
      setOfferDraft(emptyOfferDraft());
    });
  }

  async function handleLeadSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const payload = buildLeadPayload(leadDraft, data.offers, data.flows, copy);
    await runAction(async () => {
      if (leadDraft.id) {
        await updateWorkspaceMVPLead(leadDraft.id, payload);
      } else {
        await createWorkspaceMVPLead(payload);
      }
      setLeadDraft(emptyLeadDraft());
    });
  }

  async function handlePostbackSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const payload = buildPostbackPayload(postbackDraft, data.flows, copy);
    await runAction(async () => {
      if (postbackDraft.id) {
        await updateWorkspaceMVPPostback(postbackDraft.id, payload);
      } else {
        await createWorkspaceMVPPostback(payload);
      }
      setPostbackDraft(emptyPostbackDraft());
    });
  }

  async function handlePayoutSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const payload = buildPayoutPayload(payoutDraft, data.users, copy);
    await runAction(async () => {
      if (payoutDraft.id) {
        await updateWorkspaceMVPPayout(payoutDraft.id, payload);
      } else {
        await createWorkspaceMVPPayout(payload);
      }
      setPayoutDraft(emptyPayoutDraft());
    });
  }

  async function handleCompanySubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const payload = buildCompanyPayload(companyDraft);
    await runAction(async () => {
      if (companyDraft.id) {
        await updateWorkspaceMVPCompany(companyDraft.id, payload);
      } else {
        await createWorkspaceMVPCompany(payload);
      }
      setCompanyDraft(emptyCompanyDraft());
    });
  }

  async function handleUserSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const payload = buildUserPayload(userDraft, data.companies, copy);
    await runAction(async () => {
      if (userDraft.id) {
        await updateWorkspaceMVPUser(userDraft.id, payload);
      } else {
        await createWorkspaceMVPUser(payload);
      }
      setUserDraft(emptyUserDraft());
    });
  }

  async function handleCreateOrder(lead: WorkspaceMVPLead) {
    const offer = data.offers.find((item) => item.id === lead.offer_id);
    await runAction(async () => {
      await createWorkspaceMVPOrder({
        lead_id: lead.id,
        status: "pending",
        amount_cents: offer?.payout_cents ?? 0,
        currency: offer?.currency ?? "USD"
      });
    });
  }

  function renderSection(): ReactNode {
    if (data.isLoading) {
      return (
        <section className="notice mvp-state">
          <RefreshCw aria-hidden="true" size={18} />
          <span>{copy.messages.loading}</span>
        </section>
      );
    }

    if (data.isError) {
      return (
        <section className="notice notice-error">
          <AlertTriangle aria-hidden="true" size={18} />
          <span>{copy.messages.loadFailed}</span>
        </section>
      );
    }

    if (workspace === "webmaster") {
      if (activeSection === "work") {
        return renderWebmasterFlows();
      }
      if (activeSection === "entities") {
        return renderWebmasterPostbacks();
      }
      if (activeSection === "reports") {
        return renderWebmasterPayouts();
      }
      if (activeSection === "settings") {
        return renderWebmasterSettings();
      }
      return renderOverview();
    }

    if (workspace === "advertiser") {
      if (activeSection === "work") {
        return renderAdvertiserOffers();
      }
      if (activeSection === "entities") {
        return renderAdvertiserLeads();
      }
      if (activeSection === "reports") {
        return renderAdvertiserPostbacks();
      }
      if (activeSection === "settings") {
        return renderAdvertiserStaff();
      }
      return renderOverview();
    }

    if (activeSection === "work") {
      return renderAdminCompanies();
    }
    if (activeSection === "entities") {
      return renderAdminOperations();
    }
    if (activeSection === "reports") {
      return renderAdminReports();
    }
    if (activeSection === "users") {
      return renderAdminUsers();
    }
    return renderOverview();
  }

  function renderOverview() {
    return (
      <div className="mvp-section-shell">
        <MetricGrid cards={metricCardsForWorkspace(workspace, data.stats, data, copy)} />
        <div className="dashboard-grid">
          <Panel title={workspace === "webmaster" ? copy.labels.flows : workspace === "advertiser" ? copy.labels.leads : copy.labels.companies}>
            <div className="mvp-stat-table">
              {workspace === "webmaster"
                ? renderFlowRows(data.flows.slice(0, 5))
                : workspace === "advertiser"
                  ? renderLeadRows(filteredLeads.slice(0, 5))
                  : renderCompanyRows(data.companies.slice(0, 5))}
            </div>
          </Panel>
          <Panel title={copy.labels.audit}>
            <AuditList copy={copy} data={data} />
          </Panel>
        </div>
      </div>
    );
  }

  function renderWebmasterFlows() {
    return (
      <div className="mvp-grid mvp-grid-form">
        <Panel title={flowDraft.id ? copy.actions.edit : copy.labels.flows}>
          <form className="mvp-panel-body mvp-form" onSubmit={handleFlowSubmit}>
            <TextField label={copy.fields.name} value={flowDraft.name} onChange={(name) => setFlowDraft({ ...flowDraft, name })} required />
            <SelectField
              label={copy.fields.offer}
              value={flowDraft.offer_id}
              onChange={(offer_id) => setFlowDraft({ ...flowDraft, offer_id })}
              options={data.offers.map((offer) => ({ value: offer.id, label: offer.name }))}
            />
            <SelectField
              label={copy.fields.status}
              value={flowDraft.status}
              onChange={(status) => setFlowDraft({ ...flowDraft, status: status as WorkspaceMVPOfferStatus })}
              options={offerStatuses.map((status) => ({ value: status, label: statusLabel(copy, status) }))}
            />
            <TextField
              label={copy.fields.source}
              value={flowDraft.traffic_source}
              onChange={(traffic_source) => setFlowDraft({ ...flowDraft, traffic_source })}
            />
            <TextField
              label={copy.fields.landing}
              type="url"
              value={flowDraft.landing_url}
              onChange={(landing_url) => setFlowDraft({ ...flowDraft, landing_url })}
              required
            />
            <TextField
              label={copy.fields.dailyCap}
              min={0}
              type="number"
              value={flowDraft.daily_cap}
              onChange={(daily_cap) => setFlowDraft({ ...flowDraft, daily_cap })}
            />
            <FormActions copy={copy} editing={Boolean(flowDraft.id)} onCancel={() => setFlowDraft(emptyFlowDraft())} />
          </form>
        </Panel>
        <Panel title={copy.labels.availableOffers}>
          <div className="mvp-card-list mvp-panel-body">
            {data.offers.map((offer) => (
              <article className="mvp-mini-card" key={offer.id}>
                <div>
                  <strong>{offer.name}</strong>
                  <small>
                    {offer.geo.join(", ")} · {formatMoney(offer.payout_cents, offer.currency)}
                  </small>
                </div>
                <StatusChip copy={copy} status={offer.status} />
                <Button
                  icon={<Plus aria-hidden="true" size={15} />}
                  onClick={() =>
                    setFlowDraft({
                      ...emptyFlowDraft(),
                      offer_id: offer.id,
                      name: `${offer.name} flow`,
                      landing_url: "https://flows.example/new-flow"
                    })
                  }
                  size="sm"
                  variant="secondary"
                >
                  {copy.actions.add}
                </Button>
              </article>
            ))}
            {data.offers.length === 0 ? <EmptyState copy={copy} /> : null}
          </div>
        </Panel>
        <Panel title={copy.labels.flows}>
          <div className="mvp-panel-body">{renderFlowTable(data.flows)}</div>
        </Panel>
      </div>
    );
  }

  function renderWebmasterPostbacks() {
    return (
      <div className="mvp-grid mvp-grid-form">
        <Panel title={postbackDraft.id ? copy.actions.edit : copy.labels.postbacks}>
          <form className="mvp-panel-body mvp-form" onSubmit={handlePostbackSubmit}>
            <SelectField
              label={copy.fields.flow}
              value={postbackDraft.flow_id}
              onChange={(flow_id) => setPostbackDraft({ ...postbackDraft, flow_id })}
              options={data.flows.map((flow) => ({ value: flow.id, label: flow.name }))}
            />
            <TextField
              label={copy.fields.event}
              value={postbackDraft.event}
              onChange={(event) => setPostbackDraft({ ...postbackDraft, event })}
              required
            />
            <TextField
              label={copy.fields.target}
              type="url"
              value={postbackDraft.target_url}
              onChange={(target_url) => setPostbackDraft({ ...postbackDraft, target_url })}
              required
            />
            <SelectField
              label={copy.fields.status}
              value={postbackDraft.status}
              onChange={(status) => setPostbackDraft({ ...postbackDraft, status: status as WorkspaceMVPPostbackStatus })}
              options={postbackStatuses.map((status) => ({ value: status, label: statusLabel(copy, status) }))}
            />
            <FormActions copy={copy} editing={Boolean(postbackDraft.id)} onCancel={() => setPostbackDraft(emptyPostbackDraft())} />
          </form>
        </Panel>
        <Panel title={copy.labels.postbacks}>
          <div className="mvp-panel-body">{renderPostbackTable(data.postbacks)}</div>
        </Panel>
      </div>
    );
  }

  function renderWebmasterPayouts() {
    const webmasterUsers = data.users.filter((user) => user.role === "webmaster" || user.workspaces.includes("webmaster"));
    return (
      <div className="mvp-grid mvp-grid-form">
        <Panel title={payoutDraft.id ? copy.actions.edit : copy.labels.payouts}>
          <form className="mvp-panel-body mvp-form" onSubmit={handlePayoutSubmit}>
            <SelectField
              label={copy.fields.user}
              value={payoutDraft.user_id}
              onChange={(user_id) => setPayoutDraft({ ...payoutDraft, user_id })}
              options={webmasterUsers.map((user) => ({ value: user.id, label: user.display_name }))}
            />
            <TextField label={copy.fields.amount} min={1} type="number" value={payoutDraft.amount} onChange={(amount) => setPayoutDraft({ ...payoutDraft, amount })} />
            <TextField
              label={copy.fields.currency}
              maxLength={3}
              value={payoutDraft.currency}
              onChange={(currency) => setPayoutDraft({ ...payoutDraft, currency: currency.toUpperCase() })}
            />
            <SelectField
              label={copy.fields.method}
              value={payoutDraft.method}
              onChange={(method) => setPayoutDraft({ ...payoutDraft, method: method as WorkspaceMVPPayoutMethod })}
              options={payoutMethods.map((method) => ({ value: method, label: method.replace("_", " ") }))}
            />
            <SelectField
              label={copy.fields.status}
              value={payoutDraft.status}
              onChange={(status) => setPayoutDraft({ ...payoutDraft, status: status as WorkspaceMVPPayoutStatus })}
              options={payoutStatuses.map((status) => ({ value: status, label: statusLabel(copy, status) }))}
            />
            <FormActions copy={copy} editing={Boolean(payoutDraft.id)} onCancel={() => setPayoutDraft(emptyPayoutDraft())} />
          </form>
        </Panel>
        <Panel title={copy.labels.payouts}>
          <div className="mvp-panel-body">{renderPayoutTable(data.payouts)}</div>
        </Panel>
      </div>
    );
  }

  function renderWebmasterSettings() {
    return (
      <div className="mvp-section-shell">
        <Panel title={copy.labels.trackingLink}>
          <div className="mvp-panel-body">{renderFlowTable(data.flows)}</div>
        </Panel>
        <Panel title={copy.labels.leads}>
          <div className="mvp-stat-table">{renderLeadRows(filteredLeads)}</div>
        </Panel>
      </div>
    );
  }

  function renderAdvertiserOffers() {
    return (
      <div className="mvp-grid mvp-grid-form">
        <Panel title={offerDraft.id ? copy.actions.edit : copy.labels.availableOffers}>
          <form className="mvp-panel-body mvp-form" onSubmit={handleOfferSubmit}>
            <TextField label={copy.fields.name} value={offerDraft.name} onChange={(name) => setOfferDraft({ ...offerDraft, name })} required />
            <SelectField
              label={copy.fields.company}
              value={offerDraft.company_id}
              onChange={(company_id) => setOfferDraft({ ...offerDraft, company_id })}
              options={data.companies.map((company) => ({ value: company.id, label: company.name }))}
            />
            <SelectField
              label={copy.fields.status}
              value={offerDraft.status}
              onChange={(status) => setOfferDraft({ ...offerDraft, status: status as WorkspaceMVPOfferStatus })}
              options={offerStatuses.map((status) => ({ value: status, label: statusLabel(copy, status) }))}
            />
            <TextField label={copy.fields.geo} value={offerDraft.geo} onChange={(geo) => setOfferDraft({ ...offerDraft, geo })} required />
            <TextField label={copy.fields.payout} min={0} type="number" value={offerDraft.payout} onChange={(payout) => setOfferDraft({ ...offerDraft, payout })} />
            <TextField
              label={copy.fields.currency}
              maxLength={3}
              value={offerDraft.currency}
              onChange={(currency) => setOfferDraft({ ...offerDraft, currency: currency.toUpperCase() })}
            />
            <FormActions copy={copy} editing={Boolean(offerDraft.id)} onCancel={() => setOfferDraft(emptyOfferDraft())} />
          </form>
        </Panel>
        <Panel title={copy.labels.availableOffers}>
          <div className="mvp-panel-body">{renderOfferTable(data.offers)}</div>
        </Panel>
      </div>
    );
  }

  function renderAdvertiserLeads() {
    return (
      <div className="mvp-section-shell">
        <div className="mvp-grid mvp-grid-form">
          <Panel title={leadDraft.id ? copy.actions.edit : copy.labels.leads}>
            <form className="mvp-panel-body mvp-form" onSubmit={handleLeadSubmit}>
              <SelectField
                label={copy.fields.offer}
                value={leadDraft.offer_id}
                onChange={(offer_id) => setLeadDraft({ ...leadDraft, offer_id, flow_id: "" })}
                options={data.offers.map((offer) => ({ value: offer.id, label: offer.name }))}
              />
              <SelectField
                label={copy.fields.flow}
                value={leadDraft.flow_id}
                onChange={(flow_id) => setLeadDraft({ ...leadDraft, flow_id })}
                options={data.flows
                  .filter((flow) => !leadDraft.offer_id || flow.offer_id === leadDraft.offer_id)
                  .map((flow) => ({ value: flow.id, label: flow.name }))}
              />
              <TextField label="CRM ID" value={leadDraft.customer_ref} onChange={(customer_ref) => setLeadDraft({ ...leadDraft, customer_ref })} required />
              <TextField label={copy.fields.country} maxLength={2} value={leadDraft.country} onChange={(country) => setLeadDraft({ ...leadDraft, country: country.toUpperCase() })} />
              <TextField label={copy.fields.source} value={leadDraft.source} onChange={(source) => setLeadDraft({ ...leadDraft, source })} />
              <SelectField
                label={copy.fields.status}
                value={leadDraft.status}
                onChange={(status) => setLeadDraft({ ...leadDraft, status: status as WorkspaceMVPLeadStatus })}
                options={leadStatuses.map((status) => ({ value: status, label: statusLabel(copy, status) }))}
              />
              <FormActions copy={copy} editing={Boolean(leadDraft.id)} onCancel={() => setLeadDraft(emptyLeadDraft())} />
            </form>
          </Panel>
          <Panel title={copy.labels.orders}>
            <div className="mvp-panel-body">{renderOrderTable(data.orders)}</div>
          </Panel>
        </div>
        <Panel
          actions={
            <label className="search-shell">
              <Search aria-hidden="true" size={16} />
              <input aria-label={copy.actions.search} onChange={(event) => setSearch(event.target.value)} placeholder={copy.actions.search} value={search} />
            </label>
          }
          title={copy.labels.leads}
        >
          <div className="mvp-stat-table">{renderLeadRows(filteredLeads)}</div>
        </Panel>
      </div>
    );
  }

  function renderAdvertiserPostbacks() {
    return (
      <div className="mvp-grid mvp-grid-form">
        <Panel title={copy.labels.integration}>
          <form className="mvp-panel-body mvp-form" onSubmit={handlePostbackSubmit}>
            <SelectField
              label={copy.fields.flow}
              value={postbackDraft.flow_id}
              onChange={(flow_id) => setPostbackDraft({ ...postbackDraft, flow_id })}
              options={data.flows.map((flow) => ({ value: flow.id, label: flow.name }))}
            />
            <TextField label={copy.fields.event} value={postbackDraft.event} onChange={(event) => setPostbackDraft({ ...postbackDraft, event })} required />
            <TextField
              label={copy.fields.target}
              type="url"
              value={postbackDraft.target_url}
              onChange={(target_url) => setPostbackDraft({ ...postbackDraft, target_url })}
              required
            />
            <SelectField
              label={copy.fields.status}
              value={postbackDraft.status}
              onChange={(status) => setPostbackDraft({ ...postbackDraft, status: status as WorkspaceMVPPostbackStatus })}
              options={postbackStatuses.map((status) => ({ value: status, label: statusLabel(copy, status) }))}
            />
            <FormActions copy={copy} editing={Boolean(postbackDraft.id)} onCancel={() => setPostbackDraft(emptyPostbackDraft())} />
          </form>
        </Panel>
        <Panel title={copy.labels.postbacks}>
          <div className="mvp-panel-body">{renderPostbackTable(data.postbacks)}</div>
        </Panel>
      </div>
    );
  }

  function renderAdvertiserStaff() {
    const advertiserUsers = data.users.filter((user) => user.workspaces.includes("advertiser"));
    return (
      <div className="mvp-grid mvp-grid-form">
        <Panel title={copy.labels.advertiserStaff}>
          <form className="mvp-panel-body mvp-form" onSubmit={handleUserSubmit}>
            <TextField label={copy.fields.email} type="email" value={userDraft.email} onChange={(email) => setUserDraft({ ...userDraft, email })} required />
            <TextField label={copy.fields.name} value={userDraft.display_name} onChange={(display_name) => setUserDraft({ ...userDraft, display_name })} required />
            <SelectField
              label={copy.fields.company}
              value={userDraft.company_id}
              onChange={(company_id) => setUserDraft({ ...userDraft, company_id })}
              options={data.companies.map((company) => ({ value: company.id, label: company.name }))}
            />
            <SelectField
              label={copy.fields.role}
              value={userDraft.role}
              onChange={(role) => setUserDraft({ ...userDraft, role: role as WorkspaceMVPUserRole })}
              options={["advertiser_owner", "advertiser_manager"].map((role) => ({ value: role, label: roleLabel(copy, role as WorkspaceMVPUserRole) }))}
            />
            <SelectField
              label={copy.fields.status}
              value={userDraft.status}
              onChange={(status) => setUserDraft({ ...userDraft, status: status as WorkspaceMVPUserStatus })}
              options={userStatuses.map((status) => ({ value: status, label: statusLabel(copy, status) }))}
            />
            <FormActions copy={copy} editing={Boolean(userDraft.id)} onCancel={() => setUserDraft(emptyUserDraft())} />
          </form>
        </Panel>
        <Panel title={copy.labels.users}>
          <div className="mvp-panel-body">{renderUserTable(advertiserUsers)}</div>
        </Panel>
      </div>
    );
  }

  function renderAdminCompanies() {
    return (
      <div className="mvp-grid mvp-grid-form">
        <Panel title={companyDraft.id ? copy.actions.edit : copy.labels.companyForm}>
          <form className="mvp-panel-body mvp-form" onSubmit={handleCompanySubmit}>
            <TextField label={copy.fields.name} value={companyDraft.name} onChange={(name) => setCompanyDraft({ ...companyDraft, name })} required />
            <SelectField
              label={copy.fields.status}
              value={companyDraft.status}
              onChange={(status) => setCompanyDraft({ ...companyDraft, status: status as WorkspaceMVPCompanyStatus })}
              options={companyStatuses.map((status) => ({ value: status, label: statusLabel(copy, status) }))}
            />
            <TextField label={copy.fields.plan} value={companyDraft.plan} onChange={(plan) => setCompanyDraft({ ...companyDraft, plan })} />
            <FormActions copy={copy} editing={Boolean(companyDraft.id)} onCancel={() => setCompanyDraft(emptyCompanyDraft())} />
          </form>
        </Panel>
        <Panel title={copy.labels.companies}>
          <div className="mvp-panel-body">{renderCompanyTable(data.companies)}</div>
        </Panel>
      </div>
    );
  }

  function renderAdminOperations() {
    return (
      <div className="mvp-section-shell">
        <div className="mvp-grid mvp-grid-form">
          <Panel title={offerDraft.id ? copy.actions.edit : copy.labels.moderation}>
            <form className="mvp-panel-body mvp-form" onSubmit={handleOfferSubmit}>
              <TextField label={copy.fields.name} value={offerDraft.name} onChange={(name) => setOfferDraft({ ...offerDraft, name })} required />
              <SelectField
                label={copy.fields.company}
                value={offerDraft.company_id}
                onChange={(company_id) => setOfferDraft({ ...offerDraft, company_id })}
                options={data.companies.map((company) => ({ value: company.id, label: company.name }))}
              />
              <SelectField
                label={copy.fields.status}
                value={offerDraft.status}
                onChange={(status) => setOfferDraft({ ...offerDraft, status: status as WorkspaceMVPOfferStatus })}
                options={offerStatuses.map((status) => ({ value: status, label: statusLabel(copy, status) }))}
              />
              <TextField label={copy.fields.geo} value={offerDraft.geo} onChange={(geo) => setOfferDraft({ ...offerDraft, geo })} required />
              <TextField label={copy.fields.payout} min={0} type="number" value={offerDraft.payout} onChange={(payout) => setOfferDraft({ ...offerDraft, payout })} />
              <TextField
                label={copy.fields.currency}
                maxLength={3}
                value={offerDraft.currency}
                onChange={(currency) => setOfferDraft({ ...offerDraft, currency: currency.toUpperCase() })}
              />
              <FormActions copy={copy} editing={Boolean(offerDraft.id)} onCancel={() => setOfferDraft(emptyOfferDraft())} />
            </form>
          </Panel>
          <Panel title={copy.labels.moderation}>
            <div className="mvp-panel-body">{renderOfferTable(data.offers)}</div>
          </Panel>
        </div>
        <div className="mvp-grid mvp-grid-form">
          <Panel title={flowDraft.id ? copy.actions.edit : copy.labels.caps}>
            <form className="mvp-panel-body mvp-form" onSubmit={handleFlowSubmit}>
              <TextField label={copy.fields.name} value={flowDraft.name} onChange={(name) => setFlowDraft({ ...flowDraft, name })} required />
              <SelectField
                label={copy.fields.offer}
                value={flowDraft.offer_id}
                onChange={(offer_id) => setFlowDraft({ ...flowDraft, offer_id })}
                options={data.offers.map((offer) => ({ value: offer.id, label: offer.name }))}
              />
              <SelectField
                label={copy.fields.status}
                value={flowDraft.status}
                onChange={(status) => setFlowDraft({ ...flowDraft, status: status as WorkspaceMVPOfferStatus })}
                options={offerStatuses.map((status) => ({ value: status, label: statusLabel(copy, status) }))}
              />
              <TextField
                label={copy.fields.source}
                value={flowDraft.traffic_source}
                onChange={(traffic_source) => setFlowDraft({ ...flowDraft, traffic_source })}
              />
              <TextField
                label={copy.fields.landing}
                type="url"
                value={flowDraft.landing_url}
                onChange={(landing_url) => setFlowDraft({ ...flowDraft, landing_url })}
                required
              />
              <TextField
                label={copy.fields.dailyCap}
                min={0}
                type="number"
                value={flowDraft.daily_cap}
                onChange={(daily_cap) => setFlowDraft({ ...flowDraft, daily_cap })}
              />
              <FormActions copy={copy} editing={Boolean(flowDraft.id)} onCancel={() => setFlowDraft(emptyFlowDraft())} />
            </form>
          </Panel>
          <Panel title={copy.labels.caps}>
            <div className="mvp-panel-body">{renderFlowTable(data.flows)}</div>
          </Panel>
        </div>
        <Panel title={copy.labels.orders}>
          <div className="mvp-panel-body">{renderOrderTable(data.orders)}</div>
        </Panel>
      </div>
    );
  }

  function renderAdminReports() {
    return (
      <div className="mvp-section-shell">
        <BulkLeadJobsPanel />
        <Panel title={copy.labels.audit}>
          <AuditList copy={copy} data={data} />
        </Panel>
      </div>
    );
  }

  function renderAdminUsers() {
    return (
      <div className="mvp-section-shell">
        <div className="mvp-grid mvp-grid-form">
          <Panel title={copy.labels.userForm}>
            <form className="mvp-panel-body mvp-form" onSubmit={handleUserSubmit}>
              <TextField label={copy.fields.email} type="email" value={userDraft.email} onChange={(email) => setUserDraft({ ...userDraft, email })} required />
              <TextField label={copy.fields.name} value={userDraft.display_name} onChange={(display_name) => setUserDraft({ ...userDraft, display_name })} required />
              <SelectField
                label={copy.fields.company}
                value={userDraft.company_id}
                onChange={(company_id) => setUserDraft({ ...userDraft, company_id })}
                options={data.companies.map((company) => ({ value: company.id, label: company.name }))}
              />
              <SelectField
                label={copy.fields.role}
                value={userDraft.role}
                onChange={(role) => setUserDraft({ ...userDraft, role: role as WorkspaceMVPUserRole })}
                options={userRoles.map((role) => ({ value: role, label: roleLabel(copy, role) }))}
              />
              <SelectField
                label={copy.fields.status}
                value={userDraft.status}
                onChange={(status) => setUserDraft({ ...userDraft, status: status as WorkspaceMVPUserStatus })}
                options={userStatuses.map((status) => ({ value: status, label: statusLabel(copy, status) }))}
              />
              <FormActions copy={copy} editing={Boolean(userDraft.id)} onCancel={() => setUserDraft(emptyUserDraft())} />
            </form>
          </Panel>
          <Panel title={copy.labels.users}>
            <div className="mvp-panel-body">{renderUserTable(data.users)}</div>
          </Panel>
        </div>
        {canManagePlatform ? <AdminUsersPanel actorRole={actorRole} /> : null}
      </div>
    );
  }

  function renderFlowRows(flows: WorkspaceMVPFlow[]) {
    if (flows.length === 0) {
      return <EmptyState copy={copy} />;
    }
    return flows.map((flow) => (
      <div className="mvp-row" key={flow.id}>
        <span>
          <strong>{flow.name}</strong>
          <small>{getOfferName(data.offers, flow.offer_id)}</small>
        </span>
        <StatusChip copy={copy} status={flow.status} />
        <span>{flow.daily_cap}</span>
        <span>{flow.traffic_source || "source"}</span>
      </div>
    ));
  }

  function renderLeadRows(leads: WorkspaceMVPLead[]) {
    if (leads.length === 0) {
      return <EmptyState copy={copy} />;
    }
    return leads.map((lead) => {
      const hasOrder = data.orders.some((order) => order.lead_id === lead.id);
      return (
        <div className="mvp-row mvp-row-wide" key={lead.id}>
          <span>
            <strong>{lead.customer_ref}</strong>
            <small>
              {getOfferName(data.offers, lead.offer_id)} · {getFlowName(data.flows, lead.flow_id)}
            </small>
          </span>
          <StatusChip copy={copy} status={lead.status} />
          <span>{lead.country || "-"}</span>
          <div className="row-actions">
            {leadStatuses.map((status) => (
              <Button key={status} onClick={() => runAction(() => updateLeadStatus(lead, status))} size="sm" variant={lead.status === status ? "primary" : "secondary"}>
                {statusLabel(copy, status)}
              </Button>
            ))}
            <Button icon={<Edit3 aria-hidden="true" size={14} />} onClick={() => setLeadDraft(leadDraftFromItem(lead))} size="sm">
              {copy.actions.edit}
            </Button>
            {hasOrder ? null : (
              <Button icon={<Plus aria-hidden="true" size={14} />} onClick={() => handleCreateOrder(lead)} size="sm">
                {copy.labels.orders}
              </Button>
            )}
            <Button icon={<Trash2 aria-hidden="true" size={14} />} onClick={() => runAction(() => deleteWorkspaceMVPLead(lead.id))} size="sm" variant="danger">
              {copy.actions.delete}
            </Button>
          </div>
        </div>
      );
    });
  }

  function renderCompanyRows(companies: WorkspaceMVPCompany[]) {
    if (companies.length === 0) {
      return <EmptyState copy={copy} />;
    }
    return companies.map((company) => (
      <div className="mvp-row" key={company.id}>
        <span>
          <strong>{company.name}</strong>
          <small>{company.plan}</small>
        </span>
        <StatusChip copy={copy} status={company.status} />
        <span>{data.users.filter((user) => user.company_id === company.id).length}</span>
        <span>{data.offers.filter((offer) => offer.company_id === company.id).length}</span>
      </div>
    ));
  }

  function renderFlowTable(flows: WorkspaceMVPFlow[]) {
    if (flows.length === 0) {
      return <EmptyState copy={copy} />;
    }
    return (
      <div className="mvp-table-wrap">
        <table className="mvp-table mvp-table-flows">
          <thead>
            <tr>
              <th>{copy.fields.name}</th>
              <th>{copy.fields.offer}</th>
              <th>{copy.fields.status}</th>
              <th>{copy.fields.dailyCap}</th>
              <th>{copy.fields.source}</th>
              <th>{copy.labels.trackingLink}</th>
              <th>{copy.labels.actions}</th>
            </tr>
          </thead>
          <tbody>{flows.map((flow) => renderFlowTableRow(flow))}</tbody>
        </table>
      </div>
    );
  }

  function renderFlowTableRow(flow: WorkspaceMVPFlow) {
    const trackingLink = makeTrackingLink(flow);
    return (
      <tr key={flow.id}>
        <td>
          <strong>{flow.name}</strong>
        </td>
        <td>{getOfferName(data.offers, flow.offer_id)}</td>
        <td>
          <StatusChip copy={copy} status={flow.status} />
        </td>
        <td>{flow.daily_cap}</td>
        <td>{flow.traffic_source || "-"}</td>
        <td>
          <div className="mvp-generated mvp-generated-compact">
            <span>{trackingLink}</span>
            <Button icon={<Copy aria-hidden="true" size={14} />} onClick={() => copyText(trackingLink, copy, setNotice)} size="sm">
              {copy.actions.copy}
            </Button>
          </div>
        </td>
        <td>
          <div className="row-actions">
            {offerStatuses.map((status) => (
              <Button key={status} onClick={() => runAction(() => updateFlowStatus(flow, status))} size="sm" variant={flow.status === status ? "primary" : "secondary"}>
                {statusLabel(copy, status)}
              </Button>
            ))}
            <Button icon={<Edit3 aria-hidden="true" size={14} />} onClick={() => setFlowDraft(flowDraftFromItem(flow))} size="sm">
              {copy.actions.edit}
            </Button>
            <Button icon={<Trash2 aria-hidden="true" size={14} />} onClick={() => runAction(() => deleteWorkspaceMVPFlow(flow.id))} size="sm" variant="danger">
              {copy.actions.delete}
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  function renderOfferTable(offers: WorkspaceMVPOffer[]) {
    if (offers.length === 0) {
      return <EmptyState copy={copy} />;
    }
    return (
      <div className="mvp-table-wrap">
        <table className="mvp-table">
          <thead>
            <tr>
              <th>{copy.fields.name}</th>
              <th>{copy.fields.company}</th>
              <th>{copy.fields.status}</th>
              <th>{copy.fields.geo}</th>
              <th>{copy.fields.payout}</th>
              <th>{copy.labels.actions}</th>
            </tr>
          </thead>
          <tbody>
            {offers.map((offer) => (
              <tr key={offer.id}>
                <td>
                  <strong>{offer.name}</strong>
                </td>
                <td>{getCompanyName(data.companies, offer.company_id)}</td>
                <td>
                  <StatusChip copy={copy} status={offer.status} />
                </td>
                <td>{offer.geo.join(", ")}</td>
                <td>{formatMoney(offer.payout_cents, offer.currency)}</td>
                <td>
                  <div className="row-actions">
                    <Button icon={<CheckCircle2 aria-hidden="true" size={14} />} onClick={() => runAction(() => updateOfferStatus(offer, "active"))} size="sm">
                      {copy.actions.approve}
                    </Button>
                    <Button icon={<Edit3 aria-hidden="true" size={14} />} onClick={() => setOfferDraft(offerDraftFromItem(offer))} size="sm">
                      {copy.actions.edit}
                    </Button>
                    <Button onClick={() => runAction(() => updateOfferStatus(offer, "archived"))} size="sm">
                      {copy.actions.archive}
                    </Button>
                    <Button icon={<Trash2 aria-hidden="true" size={14} />} onClick={() => runAction(() => deleteWorkspaceMVPOffer(offer.id))} size="sm" variant="danger">
                      {copy.actions.delete}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderPostbackTable(postbacks: WorkspaceMVPPostback[]) {
    if (postbacks.length === 0) {
      return <EmptyState copy={copy} />;
    }
    return (
      <div className="mvp-table-wrap">
        <table className="mvp-table">
          <thead>
            <tr>
              <th>{copy.fields.event}</th>
              <th>{copy.fields.flow}</th>
              <th>{copy.fields.status}</th>
              <th>{copy.fields.target}</th>
              <th>{copy.labels.actions}</th>
            </tr>
          </thead>
          <tbody>
            {postbacks.map((postback) => (
              <tr key={postback.id}>
                <td>
                  <strong>{postback.event}</strong>
                </td>
                <td>{getFlowName(data.flows, postback.flow_id)}</td>
                <td>
                  <StatusChip copy={copy} status={postback.status} />
                </td>
                <td className="mvp-url-cell">{postback.target_url}</td>
                <td>
                  <div className="row-actions">
                    {postbackStatuses.map((status) => (
                      <Button key={status} onClick={() => runAction(() => updatePostbackStatus(postback, status))} size="sm" variant={postback.status === status ? "primary" : "secondary"}>
                        {statusLabel(copy, status)}
                      </Button>
                    ))}
                    <Button icon={<Edit3 aria-hidden="true" size={14} />} onClick={() => setPostbackDraft(postbackDraftFromItem(postback))} size="sm">
                      {copy.actions.edit}
                    </Button>
                    <Button icon={<Trash2 aria-hidden="true" size={14} />} onClick={() => runAction(() => deleteWorkspaceMVPPostback(postback.id))} size="sm" variant="danger">
                      {copy.actions.delete}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderPayoutTable(payouts: WorkspaceMVPPayout[]) {
    if (payouts.length === 0) {
      return <EmptyState copy={copy} />;
    }
    return (
      <div className="mvp-table-wrap">
        <table className="mvp-table">
          <thead>
            <tr>
              <th>{copy.fields.user}</th>
              <th>{copy.fields.amount}</th>
              <th>{copy.fields.method}</th>
              <th>{copy.fields.status}</th>
              <th>{copy.labels.actions}</th>
            </tr>
          </thead>
          <tbody>
            {payouts.map((payout) => (
              <tr key={payout.id}>
                <td>
                  <strong>{getUserName(data.users, payout.user_id)}</strong>
                </td>
                <td>{formatMoney(payout.amount_cents, payout.currency)}</td>
                <td>{payout.method.replace("_", " ")}</td>
                <td>
                  <StatusChip copy={copy} status={payout.status} />
                </td>
                <td>
                  <div className="row-actions">
                    {payoutStatuses.map((status) => (
                      <Button key={status} onClick={() => runAction(() => updatePayoutStatus(payout, status))} size="sm" variant={payout.status === status ? "primary" : "secondary"}>
                        {statusLabel(copy, status)}
                      </Button>
                    ))}
                    <Button icon={<Edit3 aria-hidden="true" size={14} />} onClick={() => setPayoutDraft(payoutDraftFromItem(payout))} size="sm">
                      {copy.actions.edit}
                    </Button>
                    <Button icon={<Trash2 aria-hidden="true" size={14} />} onClick={() => runAction(() => deleteWorkspaceMVPPayout(payout.id))} size="sm" variant="danger">
                      {copy.actions.delete}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderOrderTable(orders: WorkspaceMVPOrder[]) {
    if (orders.length === 0) {
      return <EmptyState copy={copy} />;
    }
    return (
      <div className="mvp-table-wrap">
        <table className="mvp-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>{copy.fields.offer}</th>
              <th>{copy.fields.amount}</th>
              <th>{copy.fields.status}</th>
              <th>{copy.labels.actions}</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id}>
                <td>
                  <strong>{order.lead_id}</strong>
                </td>
                <td>{getOfferName(data.offers, order.offer_id)}</td>
                <td>{formatMoney(order.amount_cents, order.currency)}</td>
                <td>
                  <StatusChip copy={copy} status={order.status} />
                </td>
                <td>
                  <div className="row-actions">
                    {orderStatuses.map((status) => (
                      <Button key={status} onClick={() => runAction(() => updateOrderStatus(order, status))} size="sm" variant={order.status === status ? "primary" : "secondary"}>
                        {statusLabel(copy, status)}
                      </Button>
                    ))}
                    <Button icon={<Trash2 aria-hidden="true" size={14} />} onClick={() => runAction(() => deleteWorkspaceMVPOrder(order.id))} size="sm" variant="danger">
                      {copy.actions.delete}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderCompanyTable(companies: WorkspaceMVPCompany[]) {
    if (companies.length === 0) {
      return <EmptyState copy={copy} />;
    }
    return (
      <div className="mvp-table-wrap">
        <table className="mvp-table">
          <thead>
            <tr>
              <th>{copy.fields.name}</th>
              <th>{copy.fields.plan}</th>
              <th>{copy.fields.status}</th>
              <th>{copy.metrics.users}</th>
              <th>{copy.metrics.offers}</th>
              <th>{copy.labels.actions}</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => (
              <tr key={company.id}>
                <td>
                  <strong>{company.name}</strong>
                </td>
                <td>{company.plan || "-"}</td>
                <td>
                  <StatusChip copy={copy} status={company.status} />
                </td>
                <td>{data.users.filter((user) => user.company_id === company.id).length}</td>
                <td>{data.offers.filter((offer) => offer.company_id === company.id).length}</td>
                <td>
                  <div className="row-actions">
                    {companyStatuses.map((status) => (
                      <Button key={status} onClick={() => runAction(() => updateCompanyStatus(company, status))} size="sm" variant={company.status === status ? "primary" : "secondary"}>
                        {statusLabel(copy, status)}
                      </Button>
                    ))}
                    <Button icon={<Edit3 aria-hidden="true" size={14} />} onClick={() => setCompanyDraft(companyDraftFromItem(company))} size="sm">
                      {copy.actions.edit}
                    </Button>
                    <Button icon={<Trash2 aria-hidden="true" size={14} />} onClick={() => runAction(() => deleteWorkspaceMVPCompany(company.id))} size="sm" variant="danger">
                      {copy.actions.delete}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderUserTable(users: WorkspaceMVPUser[]) {
    if (users.length === 0) {
      return <EmptyState copy={copy} />;
    }
    return (
      <div className="mvp-table-wrap">
        <table className="mvp-table">
          <thead>
            <tr>
              <th>{copy.fields.user}</th>
              <th>{copy.fields.email}</th>
              <th>{copy.fields.company}</th>
              <th>{copy.fields.role}</th>
              <th>{copy.fields.status}</th>
              <th>{copy.labels.actions}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <strong>{user.display_name}</strong>
                </td>
                <td>{user.email}</td>
                <td>{getCompanyName(data.companies, user.company_id)}</td>
                <td>{roleLabel(copy, user.role)}</td>
                <td>
                  <StatusChip copy={copy} status={user.status} />
                </td>
                <td>
                  <div className="row-actions">
                    {userStatuses.map((status) => (
                      <Button key={status} onClick={() => runAction(() => updateUserStatus(user, status))} size="sm" variant={user.status === status ? "primary" : "secondary"}>
                        {statusLabel(copy, status)}
                      </Button>
                    ))}
                    <Button icon={<Edit3 aria-hidden="true" size={14} />} onClick={() => setUserDraft(userDraftFromItem(user))} size="sm">
                      {copy.actions.edit}
                    </Button>
                    <Button icon={<Trash2 aria-hidden="true" size={14} />} onClick={() => runAction(() => deleteWorkspaceMVPUser(user.id))} size="sm" variant="danger">
                      {copy.actions.delete}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <main className="workspace-main mvp-console" aria-busy={data.isLoading ? "true" : undefined}>
      <section className="workspace-command mvp-command">
        <div className="command-copy">
          <span className="eyebrow">{copy.labels.reports}</span>
          <h1 data-testid="workspace-title">{copy.titles[workspace]}</h1>
          <p>{copy.subtitles[workspace]}</p>
          <div className="command-meta">
            <span>{statusText}</span>
            <span>{copy.metrics.offers}: {data.offers.length}</span>
            <span>{copy.metrics.leads}: {data.leads.length}</span>
          </div>
        </div>
        <div className="command-card mvp-command-card">
          <span>{copy.labels.audit}</span>
          <strong>{data.stats?.counts.orders ?? data.orders.length}</strong>
          <span>{copy.metrics.orders}</span>
          <Button icon={<RefreshCw aria-hidden="true" size={15} />} onClick={() => runAction(invalidateWorkspaceMVP, copy.actions.refresh)} variant="secondary">
            {copy.actions.refresh}
          </Button>
        </div>
      </section>

      {error ? (
        <section className="notice notice-error">
          <AlertTriangle aria-hidden="true" size={18} />
          <span>{error}</span>
        </section>
      ) : null}
      {notice ? (
        <section className="notice notice-success">
          <CheckCircle2 aria-hidden="true" size={18} />
          <span>{notice}</span>
        </section>
      ) : null}

      <section className="mvp-section-shell" data-testid={`workspace-section-${activeSection}`}>
        {renderSection()}
      </section>
    </main>
  );
}

function useWorkspaceMVPData() {
  const offers = useQuery({ queryKey: ["workspace-mvp", "offers"], queryFn: () => listWorkspaceMVPOffers() });
  const flows = useQuery({ queryKey: ["workspace-mvp", "flows"], queryFn: () => listWorkspaceMVPFlows() });
  const leads = useQuery({ queryKey: ["workspace-mvp", "leads"], queryFn: () => listWorkspaceMVPLeads() });
  const orders = useQuery({ queryKey: ["workspace-mvp", "orders"], queryFn: () => listWorkspaceMVPOrders() });
  const postbacks = useQuery({ queryKey: ["workspace-mvp", "postbacks"], queryFn: () => listWorkspaceMVPPostbacks() });
  const payouts = useQuery({ queryKey: ["workspace-mvp", "payouts"], queryFn: () => listWorkspaceMVPPayouts() });
  const users = useQuery({ queryKey: ["workspace-mvp", "users"], queryFn: () => listWorkspaceMVPUsers() });
  const companies = useQuery({ queryKey: ["workspace-mvp", "companies"], queryFn: () => listWorkspaceMVPCompanies() });
  const stats = useQuery({ queryKey: ["workspace-mvp", "stats"], queryFn: fetchWorkspaceMVPStats });
  const queries = [offers, flows, leads, orders, postbacks, payouts, users, companies, stats];

  return {
    companies: companies.data ?? [],
    flows: flows.data ?? [],
    isError: queries.some((query) => query.isError),
    isLoading: queries.some((query) => query.isLoading),
    leads: leads.data ?? [],
    offers: offers.data ?? [],
    orders: orders.data ?? [],
    payouts: payouts.data ?? [],
    postbacks: postbacks.data ?? [],
    stats: stats.data,
    users: users.data ?? []
  };
}

type WorkspaceMVPData = ReturnType<typeof useWorkspaceMVPData>;

function Panel({ actions, children, title }: { actions?: ReactNode; children: ReactNode; title: string }) {
  return (
    <section className="data-panel mvp-panel">
      <div className="panel-title panel-title-rich">
        <h2>{title}</h2>
        {actions ? <div className="panel-actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

function MetricGrid({ cards }: { cards: Array<{ label: string; value: string; delta: string }> }) {
  return (
    <div className="metric-grid">
      {cards.map((card) => (
        <article className="metric-card" key={card.label}>
          <div className="metric-card-head">
            <span>{card.label}</span>
            <small>{card.delta}</small>
          </div>
          <strong>{card.value}</strong>
        </article>
      ))}
    </div>
  );
}

function TextField({
  label,
  onChange,
  value,
  ...props
}: {
  label: string;
  onChange: (value: string) => void;
  value: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value">) {
  return (
    <label className="form-field">
      <span>{label}</span>
      <input {...props} onChange={(event) => onChange(event.target.value)} value={value} />
    </label>
  );
}

function SelectField({
  label,
  onChange,
  options,
  value
}: {
  label: string;
  onChange: (value: string) => void;
  options: Array<{ label: string; value: string }>;
  value: string;
}) {
  return (
    <label className="form-field">
      <span>{label}</span>
      <select onChange={(event) => onChange(event.target.value)} value={value}>
        <option value="">-</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function FormActions({ copy, editing, onCancel }: { copy: CopySet; editing: boolean; onCancel: () => void }) {
  return (
    <div className="form-actions">
      <Button icon={<Save aria-hidden="true" size={15} />} type="submit" variant="primary">
        {editing ? copy.actions.save : copy.actions.create}
      </Button>
      {editing ? (
        <Button icon={<X aria-hidden="true" size={15} />} onClick={onCancel} variant="secondary">
          {copy.actions.cancel}
        </Button>
      ) : null}
    </div>
  );
}

function StatusChip({ copy, status }: { copy: CopySet; status: string }) {
  return <span className={`status-chip status-${status}`}>{statusLabel(copy, status)}</span>;
}

function EmptyState({ copy }: { copy: CopySet }) {
  return <div className="empty-state">{copy.labels.empty}</div>;
}

function AuditList({ copy, data }: { copy: CopySet; data: WorkspaceMVPData }) {
  const items = [
    ...data.offers.map((item) => ({ id: item.id, label: `${copy.metrics.offers}: ${item.name}`, status: item.status, updated_at: item.updated_at })),
    ...data.flows.map((item) => ({ id: item.id, label: `${copy.metrics.flows}: ${item.name}`, status: item.status, updated_at: item.updated_at })),
    ...data.users.map((item) => ({ id: item.id, label: `${copy.metrics.users}: ${item.display_name}`, status: item.status, updated_at: item.updated_at }))
  ]
    .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))
    .slice(0, 8);

  if (items.length === 0) {
    return <EmptyState copy={copy} />;
  }

  return (
    <div className="insight-list">
      {items.map((item, index) => (
        <article className="insight-item" key={item.id}>
          <span>{index + 1}</span>
          <div>
            <strong>{item.label}</strong>
            <small>{statusLabel(copy, item.status)}</small>
          </div>
        </article>
      ))}
    </div>
  );
}

function metricCardsForWorkspace(workspace: Workspace, stats: WorkspaceMVPStats | undefined, data: WorkspaceMVPData, copy: CopySet) {
  const counts = stats?.counts ?? {};
  if (workspace === "webmaster") {
    return [
      { label: copy.metrics.flows, value: String(counts.flows ?? data.flows.length), delta: "+ live" },
      { label: copy.metrics.leads, value: String(counts.leads ?? data.leads.length), delta: "+ live" },
      { label: copy.metrics.payouts, value: String(counts.payouts ?? data.payouts.length), delta: "+ live" }
    ];
  }
  if (workspace === "advertiser") {
    return [
      { label: copy.metrics.offers, value: String(counts.offers ?? data.offers.length), delta: "+ live" },
      { label: copy.metrics.leads, value: String(counts.leads ?? data.leads.length), delta: "+ live" },
      { label: copy.metrics.orders, value: String(counts.orders ?? data.orders.length), delta: "+ live" }
    ];
  }
  return [
    { label: copy.metrics.companies, value: String(counts.companies ?? data.companies.length), delta: "+ live" },
    { label: copy.metrics.users, value: String(counts.users ?? data.users.length), delta: "+ live" },
    { label: copy.metrics.offers, value: String(counts.offers ?? data.offers.length), delta: "+ live" }
  ];
}

function buildFlowPayload(draft: FlowDraft, offers: WorkspaceMVPOffer[], copy: CopySet): WorkspaceMVPFlowPayload {
  const offerID = draft.offer_id || offers[0]?.id || "";
  if (!offerID) {
    throw new Error(copy.messages.needOffer);
  }
  return {
    daily_cap: Math.max(0, Number.parseInt(draft.daily_cap, 10) || 0),
    landing_url: draft.landing_url.trim(),
    name: draft.name.trim(),
    offer_id: offerID,
    status: draft.status,
    traffic_source: draft.traffic_source.trim(),
    workspace: "webmaster"
  };
}

function buildOfferPayload(draft: OfferDraft, companies: WorkspaceMVPCompany[], copy: CopySet): WorkspaceMVPOfferPayload {
  const companyID = draft.company_id || companies[0]?.id || "";
  if (!companyID) {
    throw new Error(copy.messages.needCompany);
  }
  return {
    company_id: companyID,
    currency: (draft.currency || "USD").trim().toUpperCase(),
    geo: splitGeo(draft.geo),
    name: draft.name.trim(),
    payout_cents: moneyToCents(draft.payout),
    status: draft.status,
    workspace: "advertiser"
  };
}

function buildLeadPayload(draft: LeadDraft, offers: WorkspaceMVPOffer[], flows: WorkspaceMVPFlow[], copy: CopySet): WorkspaceMVPLeadPayload {
  const offerID = draft.offer_id || offers[0]?.id || "";
  if (!offerID) {
    throw new Error(copy.messages.needOffer);
  }
  const flowID = draft.flow_id || flows.find((flow) => flow.offer_id === offerID)?.id || "";
  if (!flowID) {
    throw new Error(copy.messages.needFlow);
  }
  return {
    country: draft.country.trim().toUpperCase(),
    customer_ref: draft.customer_ref.trim(),
    flow_id: flowID,
    offer_id: offerID,
    source: draft.source.trim(),
    status: draft.status,
    workspace: "advertiser"
  };
}

function buildPostbackPayload(draft: PostbackDraft, flows: WorkspaceMVPFlow[], copy: CopySet): WorkspaceMVPPostbackPayload {
  const flowID = draft.flow_id || flows[0]?.id || "";
  if (!flowID) {
    throw new Error(copy.messages.needFlow);
  }
  return {
    event: draft.event.trim(),
    flow_id: flowID,
    status: draft.status,
    target_url: draft.target_url.trim(),
    workspace: "webmaster"
  };
}

function buildPayoutPayload(draft: PayoutDraft, users: WorkspaceMVPUser[], copy: CopySet): WorkspaceMVPPayoutPayload {
  const userID = draft.user_id || users.find((user) => user.workspaces.includes("webmaster"))?.id || users[0]?.id || "";
  if (!userID) {
    throw new Error(copy.messages.needUser);
  }
  return {
    amount_cents: moneyToCents(draft.amount),
    currency: (draft.currency || "USD").trim().toUpperCase(),
    method: draft.method,
    status: draft.status,
    user_id: userID,
    workspace: "webmaster"
  };
}

function buildCompanyPayload(draft: CompanyDraft): WorkspaceMVPCompanyPayload {
  return {
    name: draft.name.trim(),
    plan: draft.plan.trim(),
    status: draft.status
  };
}

function buildUserPayload(draft: UserDraft, companies: WorkspaceMVPCompany[], copy: CopySet): WorkspaceMVPUserPayload {
  const companyID = draft.company_id || companies[0]?.id || "";
  if (!companyID) {
    throw new Error(copy.messages.needCompany);
  }
  return {
    company_id: companyID,
    display_name: draft.display_name.trim(),
    email: draft.email.trim(),
    role: draft.role,
    status: draft.status,
    workspaces: workspacesForRole(draft.role)
  };
}

function updateFlowStatus(flow: WorkspaceMVPFlow, status: WorkspaceMVPOfferStatus) {
  return updateWorkspaceMVPFlow(flow.id, { ...flowPayloadFromItem(flow), status }).then(() => undefined);
}

function updateOfferStatus(offer: WorkspaceMVPOffer, status: WorkspaceMVPOfferStatus) {
  return updateWorkspaceMVPOffer(offer.id, { ...offerPayloadFromItem(offer), status }).then(() => undefined);
}

function updateLeadStatus(lead: WorkspaceMVPLead, status: WorkspaceMVPLeadStatus) {
  return updateWorkspaceMVPLead(lead.id, { ...leadPayloadFromItem(lead), status }).then(() => undefined);
}

function updateOrderStatus(order: WorkspaceMVPOrder, status: WorkspaceMVPOrderStatus) {
  return updateWorkspaceMVPOrder(order.id, { ...orderPayloadFromItem(order), status }).then(() => undefined);
}

function updatePostbackStatus(postback: WorkspaceMVPPostback, status: WorkspaceMVPPostbackStatus) {
  return updateWorkspaceMVPPostback(postback.id, { ...postbackPayloadFromItem(postback), status }).then(() => undefined);
}

function updatePayoutStatus(payout: WorkspaceMVPPayout, status: WorkspaceMVPPayoutStatus) {
  return updateWorkspaceMVPPayout(payout.id, { ...payoutPayloadFromItem(payout), status }).then(() => undefined);
}

function updateCompanyStatus(company: WorkspaceMVPCompany, status: WorkspaceMVPCompanyStatus) {
  return updateWorkspaceMVPCompany(company.id, { ...companyPayloadFromItem(company), status }).then(() => undefined);
}

function updateUserStatus(user: WorkspaceMVPUser, status: WorkspaceMVPUserStatus) {
  return updateWorkspaceMVPUser(user.id, { ...userPayloadFromItem(user), status }).then(() => undefined);
}

function emptyFlowDraft(): FlowDraft {
  return {
    daily_cap: "100",
    id: "",
    landing_url: "https://flows.example/landing",
    name: "",
    offer_id: "",
    status: "review",
    traffic_source: "native"
  };
}

function emptyOfferDraft(): OfferDraft {
  return { company_id: "", currency: "USD", geo: "RU", id: "", name: "", payout: "25", status: "review" };
}

function emptyLeadDraft(): LeadDraft {
  return { country: "RU", customer_ref: "", flow_id: "", id: "", offer_id: "", source: "native", status: "new" };
}

function emptyPostbackDraft(): PostbackDraft {
  return { event: "lead_accepted", flow_id: "", id: "", status: "active", target_url: "https://tracker.example/postback" };
}

function emptyPayoutDraft(): PayoutDraft {
  return { amount: "100", currency: "USD", id: "", method: "manual", status: "pending", user_id: "" };
}

function emptyCompanyDraft(): CompanyDraft {
  return { id: "", name: "", plan: "start", status: "review" };
}

function emptyUserDraft(): UserDraft {
  return {
    company_id: "",
    display_name: "",
    email: "",
    id: "",
    role: "webmaster",
    status: "invited"
  };
}

function flowDraftFromItem(item: WorkspaceMVPFlow): FlowDraft {
  return {
    daily_cap: String(item.daily_cap),
    id: item.id,
    landing_url: item.landing_url,
    name: item.name,
    offer_id: item.offer_id,
    status: item.status,
    traffic_source: item.traffic_source
  };
}

function offerDraftFromItem(item: WorkspaceMVPOffer): OfferDraft {
  return {
    company_id: item.company_id,
    currency: item.currency,
    geo: item.geo.join(", "),
    id: item.id,
    name: item.name,
    payout: centsToMoney(item.payout_cents),
    status: item.status
  };
}

function leadDraftFromItem(item: WorkspaceMVPLead): LeadDraft {
  return {
    country: item.country,
    customer_ref: item.customer_ref,
    flow_id: item.flow_id,
    id: item.id,
    offer_id: item.offer_id,
    source: item.source,
    status: item.status
  };
}

function postbackDraftFromItem(item: WorkspaceMVPPostback): PostbackDraft {
  return {
    event: item.event,
    flow_id: item.flow_id,
    id: item.id,
    status: item.status,
    target_url: item.target_url
  };
}

function payoutDraftFromItem(item: WorkspaceMVPPayout): PayoutDraft {
  return {
    amount: centsToMoney(item.amount_cents),
    currency: item.currency,
    id: item.id,
    method: item.method,
    status: item.status,
    user_id: item.user_id
  };
}

function companyDraftFromItem(item: WorkspaceMVPCompany): CompanyDraft {
  return { id: item.id, name: item.name, plan: item.plan, status: item.status };
}

function userDraftFromItem(item: WorkspaceMVPUser): UserDraft {
  return {
    company_id: item.company_id,
    display_name: item.display_name,
    email: item.email,
    id: item.id,
    role: item.role,
    status: item.status
  };
}

function flowPayloadFromItem(item: WorkspaceMVPFlow): WorkspaceMVPFlowPayload {
  return {
    daily_cap: item.daily_cap,
    landing_url: item.landing_url,
    name: item.name,
    offer_id: item.offer_id,
    status: item.status,
    traffic_source: item.traffic_source,
    workspace: item.workspace
  };
}

function offerPayloadFromItem(item: WorkspaceMVPOffer): WorkspaceMVPOfferPayload {
  return {
    company_id: item.company_id,
    currency: item.currency,
    geo: item.geo,
    name: item.name,
    payout_cents: item.payout_cents,
    status: item.status,
    workspace: item.workspace
  };
}

function leadPayloadFromItem(item: WorkspaceMVPLead): WorkspaceMVPLeadPayload {
  return {
    country: item.country,
    customer_ref: item.customer_ref,
    flow_id: item.flow_id,
    offer_id: item.offer_id,
    source: item.source,
    status: item.status,
    workspace: item.workspace
  };
}

function orderPayloadFromItem(item: WorkspaceMVPOrder): WorkspaceMVPOrderPayload {
  return {
    amount_cents: item.amount_cents,
    currency: item.currency,
    lead_id: item.lead_id,
    status: item.status
  };
}

function postbackPayloadFromItem(item: WorkspaceMVPPostback): WorkspaceMVPPostbackPayload {
  return {
    event: item.event,
    flow_id: item.flow_id,
    status: item.status,
    target_url: item.target_url,
    workspace: item.workspace
  };
}

function payoutPayloadFromItem(item: WorkspaceMVPPayout): WorkspaceMVPPayoutPayload {
  return {
    amount_cents: item.amount_cents,
    currency: item.currency,
    method: item.method,
    status: item.status,
    user_id: item.user_id,
    workspace: item.workspace
  };
}

function companyPayloadFromItem(item: WorkspaceMVPCompany): WorkspaceMVPCompanyPayload {
  return {
    name: item.name,
    plan: item.plan,
    status: item.status
  };
}

function userPayloadFromItem(item: WorkspaceMVPUser): WorkspaceMVPUserPayload {
  return {
    company_id: item.company_id,
    display_name: item.display_name,
    email: item.email,
    role: item.role,
    status: item.status,
    workspaces: item.workspaces
  };
}

function workspacesForRole(role: WorkspaceMVPUserRole): WorkspaceMVPWorkspace[] {
  if (role === "webmaster") {
    return ["webmaster"];
  }
  if (role === "advertiser_owner" || role === "advertiser_manager") {
    return ["advertiser"];
  }
  return ["admin"];
}

function statusLabel(copy: CopySet, status: string): string {
  return copy.status[status as keyof CopySet["status"]] ?? status;
}

function roleLabel(copy: CopySet, role: WorkspaceMVPUserRole): string {
  return copy.roles[role];
}

function splitGeo(raw: string): string[] {
  return raw
    .split(",")
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
}

function moneyToCents(raw: string): number {
  return Math.max(0, Math.round((Number.parseFloat(raw) || 0) * 100));
}

function centsToMoney(cents: number): string {
  return (cents / 100).toFixed(2).replace(/\.00$/, "");
}

function formatMoney(cents: number, currency: string): string {
  return `${(cents / 100).toFixed(2)} ${currency}`;
}

function getOfferName(offers: WorkspaceMVPOffer[], id: string): string {
  return offers.find((offer) => offer.id === id)?.name ?? id;
}

function getFlowName(flows: WorkspaceMVPFlow[], id: string): string {
  return flows.find((flow) => flow.id === id)?.name ?? id;
}

function getCompanyName(companies: WorkspaceMVPCompany[], id: string): string {
  return companies.find((company) => company.id === id)?.name ?? id;
}

function getUserName(users: WorkspaceMVPUser[], id: string): string {
  return users.find((user) => user.id === id)?.display_name ?? id;
}

function makeTrackingLink(flow: WorkspaceMVPFlow): string {
  const origin = typeof window === "undefined" ? "http://127.0.0.1:8084" : window.location.origin;
  return `${origin}/go/${encodeURIComponent(flow.id)}?click_id={click_id}&sub1={sub1}`;
}

function copyText(value: string, copy: CopySet, setNotice: (value: string) => void) {
  void navigator.clipboard?.writeText(value).then(() => setNotice(copy.messages.copied));
}
